import { useRef } from 'react';
import { useAuth } from '@guildeducationinc/guild-auth';
import { ShimmerLoader, Typography, ShowMore } from '@guildeducationinc/recess';
import useMount from '@src/hooks/useMount';
import usePathways from '@src/hooks/usePathways';
import PathwayCard from '@src/components/CareerPathways/PathwayCard/PathwayCard';
import './CareerPathways.scss';
import { USOPC_EMPLOYER_UUID } from '@src/constants';

export const CAREER_PATHWAYS_DESCRIPTION =
  'Imagine yourself in a new role - and choose a learning program that gets you there!';

export function buildHeading(employerName = '', employerId = '') {
  const showEmployerName = employerName && employerId !== USOPC_EMPLOYER_UUID;
  return `Grow your career${showEmployerName ? ` at ${employerName}` : ''}.`;
}

const CareerPathways = () => {
  const { pathways, loading } = usePathways();
  const { user } = useAuth();

  const { employerName, employerId } = user || {};

  const anchorRef = useRef<HTMLDivElement | null>(null);

  useMount(() => {
    if (location.hash === '#career-pathways' && anchorRef.current) {
      setTimeout(() => {
        anchorRef.current?.scrollIntoView();
      }, 1500);
    }
  });

  if (!loading && pathways.length < 1) {
    return null;
  }

  const sortedPathways = [...pathways].sort((a, b) => {
    const titleA = (a?.content?.title ?? '').toUpperCase();
    const titleB = (b?.content?.title ?? '').toUpperCase();

    if (titleA < titleB) {
      return -1;
    }

    if (titleA > titleB) {
      return 1;
    }

    return 0;
  });

  const PathwayCards = () => {
    return (
      <div className='pathwaysContainer'>
        {sortedPathways.map((pathway) => {
          if (pathway === null) return null;

          const {
            pathwayId,
            content: { title, shortDescription },
          } = pathway;

          if (!pathwayId || !title || !shortDescription) return null;

          return (
            <PathwayCard
              key={pathwayId}
              pathwayId={pathwayId}
              title={title}
              shortDescription={shortDescription}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className='pathwaySectionContainer' ref={anchorRef}>
      <ShimmerLoader height='10rem' loaded={!loading} label='career pathways'>
        <div className='pathwaySectionHeading'>
          <Typography variant='h2'>{buildHeading(employerName, employerId)}</Typography>
          <Typography variant='body1'>{CAREER_PATHWAYS_DESCRIPTION}</Typography>
        </div>
        {sortedPathways.length > 8 ? (
          <ShowMore initialHeight={470}>
            <PathwayCards />
          </ShowMore>
        ) : (
          <PathwayCards />
        )}
      </ShimmerLoader>
    </div>
  );
};

export default CareerPathways;
